import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import styled, { ThemeContext } from 'styled-components/macro'
import Text from 'components/atoms/text'
import { formatLicenseplate } from 'utilities/utils'
import { DEFAULT_LICENSE_PLATE } from 'config/consts'
import { EuStars } from 'components/atoms/eu-stars'
import { CarIdentityPlateSizes } from 'config/enums'

const StyledLicensePlate = styled.div`
  flex-shrink: 0;
  background-color: ${({ theme }) => theme.colors.licensePlate};
  color: ${({ theme }) => theme.colors.text};
  display: inline-block;
  position: relative;
  white-space: nowrap;
  font-family: ${({ theme }) => theme.licensePlateFont || theme.font};
  text-align: center;
  ${({ theme }) =>
    theme.licensePlateFont &&
    `
      border: 1px solid ${theme.colors.text};
      border-radius: ${theme.sizeByFactor(0.5)};
      overflow: hidden;
      padding-left: ${theme.sizeByFactor(2.5)};
    `}

  /**
    Using em values, because the the font-size can vary based
    on the required size of the component (small, medium).
    This way the paddings grow along with the font-size and
    everything will remain visually balanced.
  */
  > * {
    padding: 0.25em 0.5em;
  }

  /* The inner Text atom sets its own font-family via a class — beat it with
     a more specific selector so the license-plate font wins. */
  > p {
    font-family: ${({ theme }) => theme.licensePlateFont || theme.font};
  }

  /**
    Using em values, because the the font-size can vary based
    on the required size of the component (small, medium).
    This way the sizings grow along with the font-size and
    everything will remain visually balanced.
  */
  > small {
    display: inline-block;
    font-weight: 500;
    font-family: ${({ theme }) => theme.licensePlateFont || theme.font};
    ${({ $size, theme }) => {
      switch ($size) {
        case CarIdentityPlateSizes.COMPACT:
          return `
            font-size: ${theme.baseFontSize};
            line-height: ${theme.baseFontSize};
            padding: ${theme.sizeByFactor(0.5)} ${theme.sizeByFactor(0.875)};
          `
        case CarIdentityPlateSizes.MINI:
          return `
            font-size: ${theme.smallFontSize};
            line-height: ${theme.baseFontSize};
            padding: ${theme.sizeByFactor(0.25)} ${theme.sizeByFactor(0.625)};
          `
        default:
          return `
            height: 1.5em;
            line-height: 1.5em;
            padding: 0 0.5em;
            font-size: ${theme.baseFontSize};
          `
      }
    }}
  }
`

const compactSizes = [
  CarIdentityPlateSizes.TINY,
  CarIdentityPlateSizes.MINI,
  CarIdentityPlateSizes.COMPACT,
]

export const NLStrip = styled.span`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: ${({ theme }) => theme.sizeByFactor(2.5)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.nlStripBackground};
  padding: ${({ theme }) => theme.sizeByFactor(0.2)} 0 !important;
  gap: ${({ theme }) => theme.sizeByFactor(0.2)};
  color: ${({ theme }) => theme.colors.nlStripText};
`

export const NLLabel = styled.span`
  font-family:
    'gt-walsheim',
    -apple-system,
    BlinkMacSystemFont,
    sans-serif;
  font-weight: 600;
  font-size: ${({ theme }) => theme.sizeByFactor(0.75)};
  line-height: 1;
`

/**
 * General component to display license plates as actual license plates. This helps people
 * to easily recognise the letter-digit combinations as license plates.
 */
function LicensePlate({
  className,
  size,
  value,
  formatLicensePlate,
  ...restProps
}) {
  const theme = useContext(ThemeContext)
  const showNLStrip = Boolean(theme?.licensePlateFont)
  const formattedLicensePlate = formatLicensePlate
    ? formatLicenseplate(value)
    : value

  return (
    <StyledLicensePlate className={className} $size={size} {...restProps}>
      {showNLStrip && (
        <NLStrip aria-hidden="true">
          <EuStars />
          <NLLabel>NL</NLLabel>
        </NLStrip>
      )}
      {compactSizes.includes(size) ? (
        <small>{formatLicenseplate(value) || DEFAULT_LICENSE_PLATE}</small>
      ) : (
        <Text
          type={size === 'small' ? 'h5' : 'h2'}
          tag="p"
          text={formattedLicensePlate || DEFAULT_LICENSE_PLATE}
        />
      )}
    </StyledLicensePlate>
  )
}

LicensePlate.propTypes = {
  className: PropTypes.string,
  size: PropTypes.string,
  value: PropTypes.string,
  formatLicensePlate: PropTypes.bool,
}

LicensePlate.defaultProps = {
  className: null,
  size: null,
  value: 'XXXXXX',
  formatLicensePlate: true,
}

export default LicensePlate
